import type { PointerEvent } from 'react';
import { ArrowDown, ArrowUp, GripVertical, Pencil, Power, Trash2 } from 'lucide-react';
import type { FabButton } from '@/shared/types/fab-button';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { DropdownMenuItem, DropdownMenuSeparator } from '@/shared/ui/dropdown-menu';
import { ListCardActions } from '@/shared/components/list-card-actions';
import { SelectableCard } from '@/shared/components/selectable-card';
import { FAB_TYPE_META } from '../model/fab-button-meta';
import { FabIcon } from './fab-icon';

interface FabButtonCardProps {
  fab: FabButton;
  selected: boolean;
  onToggle: () => void;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canPersist?: boolean;
  onDragHandlePointerDown?: (e: PointerEvent<HTMLButtonElement>) => void;
}

export function FabButtonCard({
  fab,
  selected,
  onToggle,
  index,
  isFirst,
  isLast,
  onEdit,
  onDelete,
  onToggleActive,
  onMoveUp,
  onMoveDown,
  canPersist = true,
  onDragHandlePointerDown,
}: FabButtonCardProps) {
  const meta = FAB_TYPE_META[fab.type];
  const Icon = meta.icon;

  return (
    <SelectableCard
      selected={selected}
      onToggle={onToggle}
      index={index}
      ariaLabel={`Selecionar botão ${fab.label}`}
      onCardClick={canPersist ? onEdit : undefined}
      actions={
        <div className="flex items-center gap-1">
          {onDragHandlePointerDown ? (
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Arrastar para reordenar"
              onPointerDown={onDragHandlePointerDown}
              onClick={(e) => e.stopPropagation()}
              className="size-10 cursor-grab touch-none rounded-lg border-border/60 bg-background/60 shadow-sm active:cursor-grabbing"
            >
              <GripVertical className="size-4 text-muted-foreground" />
            </Button>
          ) : null}
          <ListCardActions
            menuChildren={
              <>
                <DropdownMenuItem onSelect={onEdit} disabled={!canPersist}>
                  <Pencil />
                  Editar
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={onToggleActive} disabled={!canPersist}>
                  <Power />
                  {fab.active ? 'Desativar' : 'Ativar'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={onMoveUp} disabled={!canPersist || isFirst}>
                  <ArrowUp />
                  Subir
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={onMoveDown} disabled={!canPersist || isLast}>
                  <ArrowDown />
                  Descer
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={onDelete} disabled={!canPersist} destructive>
                  <Trash2 />
                  Excluir
                </DropdownMenuItem>
              </>
            }
          />
        </div>
      }
    >
      <div className="flex min-w-0 items-center gap-3">
        <FabIcon
          iconUrl={fab.iconUrl}
          fallbackIcon={Icon}
          backgroundColor={fab.color || 'hsl(var(--primary))'}
          className="size-11 shrink-0 sm:size-12"
        />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <p className="truncate text-sm font-semibold sm:text-base">{fab.label || 'Sem rótulo'}</p>
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              #{fab.order ?? index + 1}
            </span>
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground sm:text-sm">{fab.value || '—'}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className="h-6 font-normal">
              {meta.label}
            </Badge>
            <Badge variant={fab.active ? 'success' : 'secondary'} className="h-6 font-normal">
              {fab.active ? 'Ativo' : 'Inativo'}
            </Badge>
            {!canPersist ? (
              <Badge variant="secondary" className="h-6 font-normal text-amber-700">
                Sem id
              </Badge>
            ) : null}
          </div>
        </div>
      </div>
    </SelectableCard>
  );
}
